import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AlertTriangle, Ban, CheckCircle2, ExternalLink, FileText, PencilLine, RotateCw } from 'lucide-react';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import { Badge } from '@/components/ui/badge';
import { Card } from '@/components/ui/card';
import { DataTable, type Column } from '@/components/ui/data-table';
import { EmptyState } from '@/components/ui/empty-state';
import { ErrorState } from '@/components/ui/error-state';
import { Skeleton } from '@/components/ui/skeleton';
import { Button } from '@/components/ui/button';
import { useToast } from '@/components/ui/toast';
import { PageHeader } from '@/components/page-header';
import { ApiError, request } from '@/lib/api';
import { useAuth, useCanEdit } from '@/lib/auth';
import type { Invoice } from '@/lib/schemas';
import { formatCurrency, formatDate, formatRelative } from '@/lib/utils';
import { MonthlyBilling } from '@/features/processes/monthly-billing';
import { GenerateInvoiceDialog } from './generate-invoice-dialog';
import { invoiceListSchema, invoicesQueryKey } from './invoices-api';

type InvoiceStatus = Invoice['status'];

/**
 * מקור יחיד לתרגום, לגוון ולאייקון של סטטוס חשבונית. הצבע לבדו לא
 * נושא את המשמעות — לכל מצב יש גם אייקון משלו, כי טכנאי שמסתכל על
 * המסך בשמש רואה גוונים שטוחים.
 */
const STATUS = {
  DRAFT: { labelKey: 'status.draft', tone: 'neutral', icon: PencilLine },
  FINALIZED: { labelKey: 'status.finalized', tone: 'success', icon: CheckCircle2 },
  VOID: { labelKey: 'status.void', tone: 'danger', icon: Ban },
} as const satisfies Record<
  InvoiceStatus,
  { labelKey: string; tone: 'neutral' | 'success' | 'danger'; icon: typeof FileText }
>;

function InvoiceStatusBadge({ status }: { status: InvoiceStatus }) {
  const { t } = useTranslation('invoices');
  const s = STATUS[status];
  const Icon = s.icon;
  return (
    <Badge tone={s.tone} dot={false}>
      <Icon className="size-3" aria-hidden />
      {t(s.labelKey)}
    </Badge>
  );
}

const buildColumns = (t: TFunction<'invoices'>): Array<Column<Invoice>> => [
  {
    key: 'invoiceNumber',
    header: t('columns.number'),
    cell: (inv) => (
      // מספר רץ פר-טננט — דרישה חשבונאית, ולכן הוא הזיהוי הראשי של
      // השורה. ltr-inline כדי שה-# לא יקפוץ לצד הלא נכון בעברית.
      <span className="ltr-inline tabular text-sm font-semibold text-fg">
        #{inv.invoiceNumber}
      </span>
    ),
  },
  {
    key: 'customer',
    header: t('columns.customer'),
    cell: (inv) => <span className="text-fg">{inv.customer?.name ?? '—'}</span>,
  },
  {
    key: 'period',
    header: t('columns.period'),
    cell: (inv) => (
      <span className="ltr-inline tabular text-xs text-fg-muted">
        {formatDate(inv.periodStart)} – {formatDate(inv.periodEnd)}
      </span>
    ),
  },
  {
    key: 'totalAmount',
    header: t('columns.total'),
    // numeric — יישור לסוף + tabular. בלי זה עמודת הכסף לא מתיישרת.
    numeric: true,
    // totalAmount מגיע כ-*מחרוזת* (Decimal(12,2) מהשרת) ועובר
    // כפי שהוא ל-formatCurrency. המרה ל-Number הייתה מחזירה את
    // שגיאת העיגול הבינארית שתוקנה בצד השרת.
    cell: (inv) => <span className="font-medium text-fg">{formatCurrency(inv.totalAmount)}</span>,
  },
  {
    key: 'status',
    header: t('columns.status'),
    cell: (inv) => <InvoiceStatusBadge status={inv.status} />,
  },
  {
    key: 'external',
    header: t('columns.greenInvoice'),
    cell: (inv) => <ExternalCell invoice={inv} />,
  },
  {
    key: 'createdAt',
    header: t('columns.created'),
    cell: (inv) => (
      <time dateTime={inv.createdAt} className="text-xs text-fg-subtle">
        {formatRelative(inv.createdAt)}
      </time>
    ),
  },
];

/** מספר המסמך בחשבונית ירוקה, מספר ההקצאה, קישור — או הכשל ו"נסה שוב". */
function ExternalCell({ invoice: inv }: { invoice: Invoice }) {
  const { t } = useTranslation('invoices');
  const toast = useToast();
  const qc = useQueryClient();
  const { can } = useAuth();
  const retry = useMutation({
    mutationFn: () =>
      request<{ ok: boolean; error?: string }>(`/processes/green-invoice/invoices/${inv.id}/issue`, {
        method: 'POST',
      }),
    onSuccess: (res) => {
      if (res.ok) toast.success(t('external.issued'));
      else toast.error(t('external.stillFailing'), res.error);
      void qc.invalidateQueries({ queryKey: invoicesQueryKey });
    },
    onError: (err: unknown) => toast.error(t('external.failed'), err instanceof ApiError ? err.message : undefined),
  });

  if (inv.externalProvider !== 'GREEN_INVOICE') return <span className="text-xs text-fg-subtle">—</span>;
  if (inv.externalNumber) {
    return (
      <span className="flex flex-col gap-0.5 text-xs">
        <span className="flex items-center gap-1.5">
          <span className="ltr-inline tabular font-medium text-fg">{inv.externalNumber}</span>
          {inv.externalUrl ? (
            <a
              href={inv.externalUrl}
              target="_blank"
              rel="noreferrer"
              className="text-accent hover:underline"
              aria-label={t('external.openAria', { number: inv.externalNumber })}
            >
              <ExternalLink className="size-3.5" aria-hidden />
            </a>
          ) : null}
        </span>
        {inv.allocationNumber ? (
          <span className="text-2xs text-fg-subtle">
            {t('external.allocation')} <span className="ltr-inline tabular">{inv.allocationNumber}</span>
          </span>
        ) : null}
      </span>
    );
  }
  return (
    <span className="flex flex-wrap items-center gap-1.5 text-xs">
      <span className="flex items-center gap-1 text-warning" title={inv.externalError ?? undefined}>
        <AlertTriangle className="size-3.5" aria-hidden />
        {t('external.notIssued')}
      </span>
      {can('OWNER') ? (
        <Button variant="ghost" size="sm" loading={retry.isPending} onClick={() => retry.mutate()}>
          <RotateCw aria-hidden />
          {t('external.retry')}
        </Button>
      ) : null}
    </span>
  );
}

export function InvoicesPage() {
  const canEdit = useCanEdit();
  const { t } = useTranslation('invoices');
  const columns = useMemo(() => buildColumns(t), [t]);
  const invoices = useQuery({
    queryKey: invoicesQueryKey,
    queryFn: ({ signal }) => request('/invoices', { schema: invoiceListSchema, signal }),
  });

  return (
    <div className="mx-auto max-w-6xl space-y-5">
      <PageHeader
        title={t('page.title')}
        description={t('page.description')}
        action={canEdit ? <GenerateInvoiceDialog /> : undefined}
      />

      <MonthlyBilling />

      <Card className="overflow-hidden">
        {invoices.isLoading ? (
          <div className="divide-y divide-border">
            {Array.from({ length: 8 }, (_, i) => (
              <div key={i} className="flex items-center gap-4 px-5 py-4">
                <Skeleton className="h-4 w-12" />
                <Skeleton className="h-4 flex-1" />
                <Skeleton className="h-4 w-20" />
                <Skeleton className="h-5 w-16 rounded-full" />
              </div>
            ))}
          </div>
        ) : invoices.isError ? (
          <ErrorState error={invoices.error} onRetry={() => void invoices.refetch()} />
        ) : !invoices.data || invoices.data.length === 0 ? (
          <EmptyState
            icon={FileText}
            title={t('page.empty')}
            description={t('page.emptyHint')}
            action={canEdit ? <GenerateInvoiceDialog /> : undefined}
          />
        ) : (
          <DataTable
            caption={t('page.caption')}
            columns={columns}
            rows={invoices.data}
            rowKey={(inv) => inv.id}
          />
        )}
      </Card>
    </div>
  );
}
